/**
 * iter119 — Host-based routing gate.
 *
 * If the browser is on the marketing hostnames (`amblemead.biz` or
 * `www.amblemead.biz`) — OR the URL includes `?marketing=1` for local
 * QA — this component forces the entire app to render only the
 * `AmblemeadLandingPage`. Every other route (including `/admin`,
 * `/login`, `/c/<slug>`) is silently redirected to `/` on this host.
 *
 * On any other host (production `play.amblemead.biz`, staging preview,
 * localhost) this is a transparent pass-through — the existing app
 * routes render normally.
 */
import { useEffect } from "react";
import AmblemeadLandingPage from "@/pages/AmblemeadLandingPage";

const MARKETING_HOSTS = [
  "amblemead.biz",
  "www.amblemead.biz",
];

export function isMarketingHost() {
  if (typeof window === "undefined") return false;
  const host = (window.location.hostname || "").toLowerCase();
  if (MARKETING_HOSTS.includes(host)) return true;
  // Local QA: `?marketing=1` forces the marketing gate on any host.
  const qs = new URLSearchParams(window.location.search);
  if (qs.get("marketing") === "1") return true;
  return false;
}

export default function HostGate({ children }) {
  const gated = isMarketingHost();

  // If we ARE on the marketing host but the URL has a non-root path,
  // rewrite to `/` so admin/login/c/<slug> deep links can't be reached
  // via URL manipulation. Uses history.replaceState so we don't add a
  // history entry; the visual result is the marketing home.
  useEffect(() => {
    if (!gated) return;
    const { pathname, search, hash } = window.location;
    // Keep `?marketing=1` on non-root paths so refresh still forces gate
    // when developers are QA-ing locally.
    if (pathname !== "/" && pathname !== "") {
      const qsKeep = new URLSearchParams(search);
      const keepMarketing = qsKeep.get("marketing") === "1";
      const nextSearch = keepMarketing ? "?marketing=1" : "";
      window.history.replaceState(null, "", `/${nextSearch}${hash || ""}`);
    }
  }, [gated]);

  if (gated) return <AmblemeadLandingPage />;
  return children;
}
